import {
  Card,
  CustomSettingsCard,
  HelpAndSupport,
  IntegrationCard,
  LogOutIcon,
  PersonalCompanyInfo,
  PickupDrop,
  WebHookSection,
  AccessTokenSection,
  ShippingCard,
  BrandedTrackingSection,
  ServicePreferenceSection,
} from '@/components';
import AccountCard from '@/components/SettingsCards/AccountCard';
import { FilesSection } from '@/components/SettingsCards/FilesSection';
import { TeamMemberSection } from '@/components/SettingsCards/TeamMemberSection';
import WorkspacesSection from '@/components/Workspace/WorkspacesSection';
import SettingsAddressBook from '@/pages/settings/address-book';
import SettingsProductCatalog from '@/pages/settings/product-catalog';
import { ROUTE, THEME_VARIABLE } from '@/config';
// TODO: Zendesk temporarily disabled due to migration.
// Replace with new chat provider when ready.
// import { useZendesk } from '@/hooks';
import usePermission from '@/hooks/usePermission';
import { useUserStore } from '@/stores';
import { useAuthStore } from '@/stores/authStore';
import { PermissionName } from '@/types';
import { useQueryClient } from '@tanstack/react-query';
import type { MenuProps } from 'antd';
import { Col, ConfigProvider, Flex, Row } from 'antd';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

// Import from dedicated file to avoid circular dependency
// (SettingsCards components import this enum, and settings/index.tsx imports from @/components)
import { SettingSection } from './settingSection';
export { SettingSection } from './settingSection';
const sectionComponents = {
  [SettingSection.Profile]: <PersonalCompanyInfo />,
  [SettingSection.Location]: <PickupDrop />,
  [SettingSection.TeamMembers]: <TeamMemberSection />,
  [SettingSection.File]: <FilesSection />,
  [SettingSection.AccessToken]: <AccessTokenSection />,
  [SettingSection.WebHook]: <WebHookSection />,
  [SettingSection.BrandedTracking]: <BrandedTrackingSection />,
  [SettingSection.AddressBook]: <SettingsAddressBook />,
  [SettingSection.ProductCatalog]: <SettingsProductCatalog />,
  [SettingSection.ServicePreference]: <ServicePreferenceSection />,
  [SettingSection.Workspaces]: <WorkspacesSection />,
  [SettingSection.Logout]: <></>,
};

const Settings = () => {
  const { hasPermission } = usePermission();

  // Determine the first accessible section based on permissions
  const initialSection = useMemo((): SettingSection => {
    if (hasPermission(PermissionName.DASHBOARD)) return SettingSection.Profile;
    if (hasPermission(PermissionName.USER_MANAGEMENT)) return SettingSection.TeamMembers;
    if (hasPermission(PermissionName.ORDER_AND_SHIPMENT)) return SettingSection.File;
    if (hasPermission(PermissionName.API_TOKEN)) return SettingSection.AccessToken;
    if (hasPermission(PermissionName.WEBHOOK)) return SettingSection.WebHook;
    // If user has no permissions, default to Logout
    return SettingSection.Logout;
  }, [hasPermission]);

  const [selectedSection, setSelectedSection] = useState<SettingSection>(initialSection);

  const clearToken = useAuthStore((state) => state.clearToken);
  const setUser = useUserStore((state) => state.setUser);

  const navigation = useNavigate();
  const { t } = useTranslation();
  const location = useLocation();
  const queryClient = useQueryClient();

  // TODO: Zendesk temporarily disabled due to migration.
  // const { logoutZd } = useZendesk();
  const logoutZd = () => {};

  useEffect(() => {
    const redirectData = location.state;
    if (redirectData?.activeTab && Object.values(SettingSection).includes(redirectData.activeTab)) {
      setSelectedSection(redirectData.activeTab);
    }
  }, []);

  // Validate selectedSection has permission, otherwise redirect to first accessible section
  useEffect(() => {
    const sectionPermissionMap: Record<SettingSection, string | null> = {
      [SettingSection.Profile]: PermissionName.DASHBOARD,
      [SettingSection.Location]: PermissionName.DASHBOARD,
      [SettingSection.TeamMembers]: PermissionName.USER_MANAGEMENT,
      [SettingSection.File]: PermissionName.ORDER_AND_SHIPMENT,
      [SettingSection.AccessToken]: PermissionName.API_TOKEN,
      [SettingSection.WebHook]: PermissionName.WEBHOOK,
      [SettingSection.BrandedTracking]: null, // No specific permission needed
      [SettingSection.AddressBook]: PermissionName.DASHBOARD,
      [SettingSection.ProductCatalog]: PermissionName.ORDER_AND_SHIPMENT,
      [SettingSection.ServicePreference]: PermissionName.ORDER_AND_SHIPMENT,
      // An invitation is addressed to a PERSON, and the invitee usually holds
      // nothing in the workspace inviting them — gating it would hide it from
      // exactly the people it is for.
      [SettingSection.Workspaces]: null,
      [SettingSection.Logout]: null, // Always accessible
    };

    const requiredPermission = sectionPermissionMap[selectedSection];
    if (requiredPermission && !hasPermission(requiredPermission)) {
      setSelectedSection(initialSection);
    }
  }, [selectedSection, hasPermission, initialSection]);

  const handleMenuClick: MenuProps['onClick'] = (e) => {
    setSelectedSection(e.key as SettingSection);
  };

  const handleLogOut = () => {
    clearToken?.();
    setUser(undefined);
    navigation(ROUTE.LOGIN);
    queryClient.removeQueries(['user']);
    // TODO: Zendesk temporarily disabled due to migration.
    // logoutZd();
  };

  return (
    <Row gutter={[24, 16]}>
      <Col lg={10} md={24} xs={24} className='lg:sticky lg:top-6 lg:self-start'>
        {/* <FreeOrders referText={t('refer_and_get_rewarded')} mgCode='MG-567' /> */}
        <ConfigProvider
          theme={{
            token: {
              colorPrimaryBg: THEME_VARIABLE.customYellowPrimary,
            },
          }}
        >
          <AccountCard selectedSection={selectedSection} handleMenuClick={handleMenuClick} />
          {/* //! Branded Tracking */}
          {/* <ShippingCard selectedSection={selectedSection} handleMenuClick={handleMenuClick} /> */}
          <IntegrationCard selectedSection={selectedSection} handleMenuClick={handleMenuClick} />
        </ConfigProvider>

        {/* <HelpAndSupport selectedSection={selectedSection} /> */}
        <Card extraClassName='mt-6 px-12'>
          <Flex onClick={handleLogOut} className='cursor-pointer'>
            <CustomSettingsCard
              IconLeft={<LogOutIcon width={24} height={24} color={THEME_VARIABLE.customRed} />}
              title={t('log_out')}
              iconBackgroundColor='bg-customRedShade1'
              sectionName={SettingSection.Logout}
              selectedSection={selectedSection}
            />
          </Flex>
        </Card>
      </Col>
      <Col lg={14} md={24} xs={24} className='mb-6'>
        {sectionComponents[selectedSection]}
      </Col>
    </Row>
  );
};

export default Settings;
